// "Refetch keeps the page" (decision #15) when the hook can't: a React Query hook whose key changes with a filter
// (dates, PPJK) returns `data: undefined` while the new key loads. useLastData holds the last settled value so the
// chart stays on screen, dimmed, instead of flashing a loader. Hooks are not edited (UI-only rule), so the page holds.
//
//   const { data, loading, error } = useGetUsageDashboardGraph(params);
//   const shown = useLastData(data, !loading && !error);    // the last settled rows, or undefined before the first
//   <UsageChart data={shown} loading={loading} … />          // refetching = loading && shown !== undefined
//
// Pass the hook's value as it comes: a new object on every render would never settle. Arrays from `?? []` are fine:
// two empty arrays count as the same value.
import { useState } from 'react';

/** Whether two held values are the same: identical, or both empty arrays. */
export function sameData(a: unknown, b: unknown): boolean {
  return Object.is(a, b) || (Array.isArray(a) && Array.isArray(b) && a.length === 0 && b.length === 0);
}

/** `value` while `settled`; otherwise the last value seen while settled (undefined before the first). */
export function useLastData<T>(value: T, settled: boolean): T | undefined {
  const [last, setLast] = useState<T | undefined>(settled ? value : undefined);
  // Storing information from earlier renders: set during render, guarded, so React re-renders once at most.
  if (settled && !sameData(last, value)) setLast(value);
  return settled ? value : last;
}
